import { useEffect, useState, useCallback, useMemo } from 'react';
import { format, addDays } from 'date-fns';
import { nl } from 'date-fns/locale';
import { supabase } from '../lib/supabase';
import { t } from '../i18n/nl.js';
import { toAmsterdamISO, todayStr, amsterdamDateStr } from '../utils/dateUtils.js';

const START_HOUR = 7;
const END_HOUR = 21; // laatste slot begint om 20:00
const RANGE_DAYS = 7;

function hourRange() {
  const hours = [];
  for (let h = START_HOUR; h < END_HOUR; h++) hours.push(h);
  return hours;
}

function dateStrPlusDays(dateStr, days) {
  return format(addDays(new Date(`${dateStr}T12:00:00Z`), days), 'yyyy-MM-dd');
}

function amsterdamHour(isoStr) {
  return parseInt(
    new Intl.DateTimeFormat('nl-NL', { timeZone: 'Europe/Amsterdam', hour: '2-digit', hour12: false }).format(new Date(isoStr)),
    10
  );
}

// Vind de eerste dag binnen de gegeven dagenlijst met minimaal één vrij uur-slot.
function findFirstFreeDay(dayStrs, itemsByDay) {
  for (const d of dayStrs) {
    const items = itemsByDay[d] || [];
    const busyHours = new Set();
    for (const it of items) {
      const sH = it._startHour, eH = it._endHour;
      for (let h = Math.max(sH, START_HOUR); h < Math.min(eH, END_HOUR); h++) busyHours.add(h);
    }
    if (busyHours.size < (END_HOUR - START_HOUR)) return d;
  }
  return null;
}

export default function CoachAvailabilityCalendar({
  coachId,
  baseDateStr,
  selectedStartsAt, // ISO string of null
  durationMinutes = 60,
  excludeId = null, // id van de les/afspraak die zelf niet als conflict mag tellen (bewerken)
  onSelectSlot, // (dateStr, timeStr) => void
}) {
  const [windowStart, setWindowStart] = useState(baseDateStr || todayStr());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setWindowStart(baseDateStr || todayStr());
  }, [baseDateStr]);

  const days = useMemo(
    () => Array.from({ length: RANGE_DAYS }, (_, i) => dateStrPlusDays(windowStart, i)),
    [windowStart]
  );

  const load = useCallback(async () => {
    if (!coachId || !windowStart) return;
    setLoading(true);
    setError('');
    try {
      const rangeStart = toAmsterdamISO(windowStart, '00:00');
      const rangeEnd = toAmsterdamISO(dateStrPlusDays(windowStart, RANGE_DAYS), '00:00');
      const { data, error: rpcErr } = await supabase.rpc('get_coach_availability_range', {
        p_coach_id: coachId,
        p_range_start: rangeStart,
        p_range_end: rangeEnd,
        p_exclude_id: excludeId,
      });
      if (rpcErr) throw rpcErr;
      setItems(data || []);
    } catch (e) {
      setError(t('beschikbaarheid.foutLaden') + e.message);
    } finally {
      setLoading(false);
    }
  }, [coachId, windowStart, excludeId]);

  useEffect(() => { load(); }, [load]);

  const itemsByDay = useMemo(() => {
    const byDay = {};
    for (const it of items) {
      const dayStr = amsterdamDateStr(it.starts_at);
      const startH = amsterdamHour(it.starts_at);
      const endMinutes = new Date(it.ends_at).getMinutes();
      const endH = amsterdamHour(it.ends_at) + (endMinutes > 0 ? 1 : 0);
      if (!byDay[dayStr]) byDay[dayStr] = [];
      byDay[dayStr].push({ ...it, _startHour: startH, _endHour: endH });
    }
    return byDay;
  }, [items]);

  function slotConflict(dateStr, hour) {
    const slotStart = new Date(toAmsterdamISO(dateStr, `${String(hour).padStart(2, '0')}:00`));
    const slotEnd = new Date(slotStart.getTime() + 60 * 60000);
    const dayItems = itemsByDay[dateStr] || [];
    for (const it of dayItems) {
      const s = new Date(it.starts_at);
      const e = new Date(it.ends_at);
      if (s < slotEnd && e > slotStart) return it;
    }
    return null;
  }

  const selectedDateStr = selectedStartsAt ? amsterdamDateStr(selectedStartsAt) : null;
  const selectedHour = selectedStartsAt
    ? parseInt(new Intl.DateTimeFormat('nl-NL', { timeZone: 'Europe/Amsterdam', hour: '2-digit', hour12: false }).format(new Date(selectedStartsAt)), 10)
    : null;

  const firstDayHasFreeSlot = useMemo(() => {
    if (!days.length) return true;
    const items0 = itemsByDay[days[0]] || [];
    const busyHours = new Set();
    for (const it of items0) {
      for (let h = Math.max(it._startHour, START_HOUR); h < Math.min(it._endHour, END_HOUR); h++) busyHours.add(h);
    }
    return busyHours.size < (END_HOUR - START_HOUR);
  }, [days, itemsByDay]);

  const firstFreeDay = useMemo(() => {
    if (firstDayHasFreeSlot) return null;
    return findFirstFreeDay(days, itemsByDay);
  }, [firstDayHasFreeSlot, days, itemsByDay]);

  return (
    <div className="card" style={{ padding: 16, marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>{t('beschikbaarheid.titel')}</h3>
        <div style={{ display: 'flex', gap: 4 }}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setWindowStart(w => dateStrPlusDays(w, -RANGE_DAYS))}>
            {t('beschikbaarheid.vorigePeriode')}
          </button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setWindowStart(w => dateStrPlusDays(w, RANGE_DAYS))}>
            {t('beschikbaarheid.volgendePeriode')}
          </button>
        </div>
      </div>

      <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 12 }}>
        {t('beschikbaarheid.klikOmTeKiezen')}
      </div>

      {error && <div className="error-msg" style={{ marginBottom: 12 }}>{error}</div>}

      {firstFreeDay && !loading && (
        <div style={{
          background: 'var(--color-primary)11', border: '1px solid var(--color-primary)',
          borderRadius: 'var(--radius)', padding: '8px 12px', marginBottom: 12, fontSize: 13,
        }}>
          {t('beschikbaarheid.geenVrijeSlotsVandaag')}{' '}
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            style={{ textDecoration: 'underline' }}
            onClick={() => setWindowStart(firstFreeDay)}
          >
            {t('beschikbaarheid.eerstvolgendeBeschikbaarheid')}
            {format(new Date(`${firstFreeDay}T12:00:00Z`), 'EEEE d MMM', { locale: nl })}
          </button>
        </div>
      )}

      {loading ? (
        <div className="loading-center"><div className="spinner" /></div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: `60px repeat(${RANGE_DAYS}, 1fr)`, gap: 4, minWidth: 700 }}>
            <div />
            {days.map(d => (
              <div key={d} style={{ textAlign: 'center', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'var(--color-text-muted)' }}>
                {format(new Date(`${d}T12:00:00Z`), 'EEE d MMM', { locale: nl })}
              </div>
            ))}

            {hourRange().map(hour => (
              <div key={hour} style={{ display: 'contents' }}>
                <div style={{ fontSize: 11, color: 'var(--color-text-muted)', textAlign: 'right', paddingRight: 6 }}>
                  {String(hour).padStart(2, '0')}:00
                </div>
                {days.map(d => {
                  const conflict = slotConflict(d, hour);
                  const isSelected = d === selectedDateStr && hour === selectedHour;
                  // Alleen rooster-conflicten (eigen lessen/PT) blokkeren de klik;
                  // externe agenda-items zijn een selecteerbare waarschuwing.
                  const isRooster = conflict?.source === 'rooster';
                  let bg = 'var(--color-bg)';
                  let label = '';
                  if (conflict) {
                    if (isRooster) {
                      bg = 'var(--color-danger)33';
                      label = conflict.title || t('beschikbaarheid.bezetRooster');
                    } else {
                      bg = 'var(--color-warning, #f59e0b)33';
                      label = conflict.title || t('beschikbaarheid.bezetPrive');
                    }
                  }
                  return (
                    <div
                      key={`${d}-${hour}`}
                      title={label || t('beschikbaarheid.legeSlot')}
                      onClick={() => !isRooster && onSelectSlot && onSelectSlot(d, `${String(hour).padStart(2, '0')}:00`)}
                      style={{
                        height: 28,
                        borderRadius: 4,
                        background: bg,
                        border: isSelected ? '2px solid var(--color-primary)' : '1px solid transparent',
                        cursor: isRooster ? 'default' : 'pointer',
                        fontSize: 9,
                        color: 'var(--color-text-muted)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        padding: '2px 4px',
                      }}
                    >
                      {label}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}

      {selectedDateStr && selectedHour !== null && slotConflict(selectedDateStr, selectedHour) && (
        <div style={{ marginTop: 12, fontSize: 12, color: 'var(--color-danger)' }}>
          {t('beschikbaarheid.gekozenConflict')}
        </div>
      )}
    </div>
  );
}
